<script setup lang="ts">
    import { ref, watch, computed, onMounted } from 'vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';

    export interface Item {
        label: string;
        value: string;
    }

    interface Props {
        label?: string;
        required?: boolean;
        infoText?: string;
        invalidMessage?: string | null;
        options: Item[];
        customOptionValue?: string;
        customInputPlaceholder?: string;
        addItemButtonText?: string;
        placeholder?: string;
        allowAdd: boolean;
    }

    const modelValue = defineModel<string>();
    const isDisabled = ref(false);
    const props = withDefaults(defineProps<Props>(), {
        customOptionValue: 'custom',
        customInputPlaceholder: 'Add custom value',
        addItemButtonText: 'Add',
        placeholder: 'Select an option or create new',
    });

    const emit = defineEmits(['customOptionAdded']);
    const customOptions = ref<Item[]>([]);

    const allOptions = computed(() => {
        const combinedOptions = [...customOptions.value, ...props.options];

        // Remove duplicates by value
        return combinedOptions.filter((option, index, self) => index === self.findIndex((o) => o.value === option.value));
    });

    const showCustomInput = ref(false);
    const customValue = ref('');
    const selectEl = ref<HTMLElement | null>(null);
    const customInputEl = ref<HTMLInputElement | null>(null);
    const dropdownOpen = ref(false);

    const selectedText = computed(() => {
        if (!modelValue.value) return '';
        const option = allOptions.value.find((o) => o.value === modelValue.value);
        return option ? option.label : '';
    });

    watch(modelValue, (newValue) => {
        updateCustomInputVisibility();
    });

    const updateCustomInputVisibility = () => {
        showCustomInput.value = modelValue.value === props.customOptionValue;

        if (showCustomInput.value) {
            setTimeout(() => {
                customInputEl.value?.focus();
            }, 0);
        }
    };

    const handleCustomInputChange = () => {
        if (props.allowAdd && customValue.value.trim()) {
            const newOption: Item = {
                label: customValue.value,
                value: customValue.value, // Keep as is - do not transform
            };

            const exists = allOptions.value.some((option) => option.value === newOption.value || option.label === newOption.label);

            if (!exists) {
                customOptions.value.push(newOption);
                modelValue.value = newOption.label;
                emit('customOptionAdded', newOption);

                // Reset custom input and hide it
                customValue.value = '';
                showCustomInput.value = false;
            }
        }
    };

    const getInvalidMessage = () => {
        return props.invalidMessage || '';
    };

    const toggleDropdown = () => {
        if (isDisabled.value) return;
        dropdownOpen.value = !dropdownOpen.value;
    };

    const selectItem = (item: Item) => {
        modelValue.value = item.value;
        dropdownOpen.value = false;

        // Check if this is the "New..." option
        updateCustomInputVisibility();
    };

    // Close dropdown when clicking outside
    onMounted(() => {
        document.addEventListener('click', (event) => {
            if (dropdownOpen.value && selectEl.value && !selectEl.value.contains(event.target as Node)) {
                dropdownOpen.value = false;
            }
        });
    });
</script>

<template>
    <div class="m-lg-b">
        <h3 class="label-look" :id="'dropdown-select-' + $.uid" :infoText="props.infoText">{{ props.label || 'Select' }}: {{ props.required ? '*' : '' }}</h3>

        <div class="dropdown" :class="{ open: dropdownOpen }" ref="selectEl">
            <!-- Dropdown trigger -->
            <button
                type="button"
                class="btn btn-default btn-block d-flex align-items-center justify-content-between text-left"
                :aria-labelledby="'dropdown-select-' + $.uid"
                :id="'dropdown-select-trigger-' + $.uid"
                :tabindex="isDisabled ? -1 : 0"
                :aria-expanded="dropdownOpen"
                :aria-haspopup="true"
                @click="toggleDropdown">
                <span :class="!selectedText && 'normal text-placeholder'">
                    {{ selectedText || props.placeholder }}
                </span>
                <span class="caret m-xs-l"></span>
            </button>

            <!-- Dropdown -->
            <ul class="dropdown-menu dropdown-menu-right w-full" :aria-labelledby="'dropdown-select-trigger-' + $.uid">
                <li v-for="(option, index) in allOptions" :key="option.value" :class="{ active: modelValue === option.value }" @click="selectItem(option)">
                    <a href="javascript:void(0)">{{ option.label }}</a>
                </li>
            </ul>
        </div>

        <div v-if="allowAdd && showCustomInput" class="m-sm-t">
            <label :for="'custom-input-' + $.uid">{{ props.customInputPlaceholder }}:</label>

            <div class="d-flex gap-sm">
                <input
                    :id="'custom-input-' + $.uid"
                    ref="customInputEl"
                    type="text"
                    class="form-control"
                    v-model="customValue"
                    @keyup.enter="handleCustomInputChange" />
                <button class="btn btn-primary" type="button" @click="handleCustomInputChange" :disabled="!customValue.trim()">
                    {{ props.addItemButtonText }}
                </button>
            </div>
        </div>

        <BsAlert
            :show="!!props.invalidMessage && props.invalidMessage.length > 0"
            :dismissible="false"
            :type="AlertTypes.Danger"
            :content="getInvalidMessage()" />
    </div>
</template>
